import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useCurrentTheme } from 'app/shared/theme';
import { useAnalyticsContext } from 'app/shared/utils';
import { Box, Icon, Typography } from 'app/shared/components/atoms';
import { Expandable } from 'app/fan/components/atoms';
import { ReactComponent as Minus } from 'icons/collapse.svg';
import { ReactComponent as Plus } from 'icons/expand.svg';

import { AccordionSectionTitle } from './AccordionSectionTitle';

export interface AccordionSectionProps
  extends React.ComponentPropsWithRef<'div'> {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  iconFillColor?: string;
  title: string;
  details: React.ReactNode;
  openByDefault?: boolean;
}

export const AccordionSection = ({
  icon,
  iconFillColor,
  title,
  details,
  openByDefault = false,
  id,
}: AccordionSectionProps) => {
  const { colors } = useCurrentTheme();
  const { trackAnalyticsEvent } = useAnalyticsContext();
  const { hash } = useLocation();

  const [isOpen, setIsOpen] = useState<boolean>(
    openByDefault || hash === `#${id}`
  );
  function toggle() {
    setIsOpen((isOpen) => !isOpen);
    trackAnalyticsEvent('Accordion Section Clicked', {
      title,
      isOpen: !isOpen,
    });
  }
  const togglerIcon = isOpen ? Minus : Plus;

  return (
    <Box w="100%" h="100%" bb={colors.primary.black.light} id={id}>
      <Box
        role="button"
        onClick={toggle}
        p={2}
        mb={2}
        w="100%"
        alignItems="center"
        justifyContent="space-between"
        gap={4}
        cursor="pointer"
      >
        <AccordionSectionTitle
          icon={icon}
          title={title}
          iconFillColor={iconFillColor}
        />
        <Icon
          fillColor={colors.button.primary.background}
          backgroundColor="inherit"
          strokeColor="none"
          size="md"
          icon={togglerIcon}
        />
      </Box>
      <Expandable isExpanded={isOpen} transitionSpeed={300}>
        <Typography
          as="div"
          color={colors.primary.white.base}
          pt={2}
          pr={4}
          pb={8}
          pl={12}
        >
          {details}
        </Typography>
      </Expandable>
    </Box>
  );
};
